import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { showDialog } from "@widgets/CommonDialog";
import { fnSave, fnGetTableById } from "@shared/api/restApi";
import type { ExcelUpload_S02_Params } from "@menu/XTC/SN_PRD_BAS_REG_001_TEX/excel_upload/types";
import { global, globalMenu } from "@shared/libs/Global";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { dateToString, formatYyyyMMdd } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import {
  getTablebySql,
  getInitFormData,
  CalAm,
} from "@shared/utils/CommonUtils";

interface ChangeSet {
  modifiedRowcount: number;
  insert: any[];
  update: any[];
  delete: any[];
}
interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

type S02WithModalProps = ExcelUpload_S02_Params & {
  modalRef?: React.RefObject<any>;
};

/**
 * S02 (심플/안정)
 * - S01 validRows를 컬럼정의대로 그대로 주입
 * - effGrouping으로 묶어 전표번호 채번, 전표별 line 1..N
 * - 모든 행 state='insert' 마킹 → getChange()가 insert로 잡힘
 * - 저장: getChange() 그대로 tables.header/detail 키로 전송
 */

const ExcelUpload_S02_ReviewSave = forwardRef<any, S02WithModalProps>(
  ({ modalRef, ...props }, ref) => {
    const {
      validRows,
      headerColumns,
      detailColumns,
      grouping,
      controller,
      numbering,
      tables,
      fieldMap, // { headerNoField, detailNoField, lineNoField } (필수)
    } = props;
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
    });
    // === 필수 필드명 ===
    const headerNoField = fieldMap?.headerNoField!;
    const detailNoField = fieldMap?.detailNoField!;
    const lineNoField = fieldMap?.lineNoField!;
    const today = new Date();
    const [popupParent, setPopupParent] = useState<any>(null);
    const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
      .toString()
      .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
    const { userInfo } = global();
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const syncingFromHeaderRef = useRef(false);
    // === 컬럼 필드 목록 (useMemo로 고정) ===
    const headerFields = useMemo(
      () =>
        (headerColumns ?? [])
          .map((c: any) => String(c?.field ?? ""))
          .filter((f) => f.length > 0),
      [headerColumns]
    );
    const detailFields = useMemo(
      () =>
        (detailColumns ?? [])
          .map((c: any) => String(c?.field ?? ""))
          .filter((f) => f.length > 0),
      [detailColumns]
    );

    // === 유효 그룹핑: line 키만 제외 (useMemo 고정) ===
    const effGrouping = useMemo(
      () => (grouping ?? []).filter((f) => !!f && f !== lineNoField),
      [grouping, lineNoField]
    );

    // === 헬퍼 ===
    const pick = (row: any, fields: string[]) => {
      const out: any = {};
      for (const f of fields) out[f] = row?.[f];
      return out;
    };
    const makeKey = (row: any) =>
      effGrouping.map((f) => row?.[f] ?? "").join("||");

    // numbering 안정화 (의존성 최소화)
    const numberingFn = numbering.fn;
    const { cd_module, no_line, cd_prefix, cd_menu, dateField } =
      numbering.options;

    // === 그리드 refs / state ===
    const [headerRows, setHeaderRows] = useState<any[]>([]);
    const [detailRows, setDetailRows] = useState<any[]>([]);
    const didMarkRef = useRef(false);

    // === 그룹핑 (useMemo) ===
    const groups = useMemo(() => {
      const map = new Map<string, any[]>();
      for (const r of validRows) {
        const k = makeKey(r);
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(r);
      }
      return map;
    }, [validRows, effGrouping]);
    useEffect(() => {
      if (modalRef?.current) {
        setPopupParent(modalRef.current.getPopupParent());
      }
    }, [modalRef?.current]);
    // === 데이터 구성: 헤더/디테일 ===
    useEffect(() => {
      let cancelled = false;
      const popup_apply = [
        { target: "tp_item_c", source: "tp_item" },
        { target: "dc_item_spec", source: "dc_item_spec" },
        { target: "fg_acct", source: "fg_acct" },
      ];
      const grid2 = detailColumns.map((col: any) => {
        let newCol = { ...col };
        if (col.cd_column === "cd_matl") {
          newCol.popup_apply = popup_apply;
        }
        return newCol;
      });
      setColums({
        grid1: headerColumns,
        grid2: grid2,
      });
      (async () => {
        didMarkRef.current = false;

        // 필수 필드 누락 가드
        if (!headerNoField || !detailNoField || !lineNoField) {
          setHeaderRows([]);
          setDetailRows([]);
          await showDialog(
            "alert",
            "fieldMap(headerNoField/detailNoField/lineNoField)이 필요합니다."
          );
          return;
        }

        const H: any[] = [];
        const L: any[] = [];

        for (const [, rows] of Array.from(groups.entries())) {
          const sample = rows[0] ?? {};

          // 전표 채번 (dateField의 "값"을 넣어서 호출)
          const slipNo = await numberingFn(
            cd_module,
            no_line,
            cd_prefix,
            sample?.[dateField],
            cd_menu
          );
          if (cancelled) return;

          // 헤더 1행: headerColumns.field 기준으로 "있는 그대로" 채우고 slipNo 주입
          const hdr = { ...sample, [headerNoField]: sample.no_style };
          H.push(hdr);

          // 디테일: rows 각각을 detailColumns.field 기준으로 채우고 slipNo/line 부여
          const payload = {
            id: "bom_tex",
            params: {
              cd_firm: userInfo.cd_firm,
              no_style: sample.no_style,
              no_line_bom: Number(sample.no_line_bom),
            },
          };

          const bom_tex = await fnGetTableById(payload);
          let seq = Number(bom_tex?.[0]?.no_line ?? 0);
          for (const r of rows) {
            seq += 1;
            const d: any = {
              ...r,
              [detailNoField]: sample.no_style,
              [lineNoField]: seq,
            };

            L.push(d);
          }
        }
        if (cancelled) return;

        // 그리드 비우고
        await gridRefs.current["grid1"].current?.deleteAllRows();
        await gridRefs.current["grid2"].current?.deleteAllRows();

        const newHeaders = H.map((hdr) => ({
          ...hdr,
          cd_firm: userInfo?.cd_firm,
          tp_item_p: "PRD",
        }));
        // 헤더 추가 (=> modifiedRows.added에 자동 기록)
        for (const hdr of newHeaders) {
          gridRefs.current["grid1"].current?.addRow(hdr);
        }
        // 디테일 추가 (=> modifiedRows.added에 자동 기록)
        for (const row of L) {
          row.cd_firm = userInfo?.cd_firm;
          gridRefs.current["grid2"].current?.addRow(row);
        }

        // 첫 전표 기준으로 디테일 필터/포커스
        if (H.length > 0) {
          const slip = H[0][headerNoField];
          gridRefs.current["grid1"].current?.selectAndFocusRow(0);
          gridRefs.current["grid2"].current?.applyFilters({
            [detailNoField]: slip,
          });
          gridRefs.current["grid2"].current?.selectAndFocusRow(0);
        }

        // 렌더 후 포커스/필터 + 모든 행 state='insert' 마킹
        setTimeout(() => {
          if (H.length > 0) {
            gridRefs.current["grid1"].current?.selectAndFocusRow(0);
            const slip = H[0]?.[headerNoField];
            if (slip) {
              gridRefs.current["grid2"].current?.applyFilters({
                [detailNoField]: slip,
              });
              gridRefs.current["grid2"].current?.selectAndFocusRow(0);
            }
          }
        }, 0);
      })();
      return () => {
        cancelled = true;
      };
    }, [
      groups, // validRows/effGrouping 바뀔 때만 변경
      headerFields, // useMemo로 고정
      detailFields, // useMemo로 고정
      headerNoField,
      detailNoField,
      lineNoField,
      numberingFn, // 함수 레퍼런스
      cd_module,
      no_line,
      cd_prefix,
      cd_menu,
      dateField, // 원시값
    ]);

    // === 상단 포커스 → 하단 필터 ===
    const onFocusedRowChanged = (row: any) => {
      if (!row) return;
      const slip = row?.[headerNoField];
      gridRefs.current["grid2"].current?.applyFilters({
        [detailNoField]: slip,
      });
      gridRefs.current["grid2"].current?.selectAndFocusRow(0);
    };

    // === 저장 ===
    const onSave = async (): Promise<boolean> => {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();

      const prdBomHTexChanges = gridRefs.current["grid1"].current?.getChange();
      const prdBomLTexChanges = gridRefs.current["grid2"].current?.getChange();
      const requiredValuesCheckH =
        gridRefs.current["grid1"].current?.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      // Validate required fields (Line)
      const requiredValuesCheckL =
        gridRefs.current["grid2"].current?.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;
      if (
        (prdBomHTexChanges?.modifiedRowcount ?? 0) === 0 &&
        (prdBomLTexChanges?.modifiedRowcount ?? 0) === 0
      ) {
        await showDialog("alert", "변경된 내용이 없습니다.");
        return false;
      }
      const saveData = {
        prd_bomh_tex: prdBomHTexChanges,
        prd_bom_tex: prdBomLTexChanges,
      };
      const ok = await fnSave("SN_PRD_BAS_REG_001_TEX_SAVE_02", saveData);
      if (!ok) return false;
      gridRefs.current["grid1"].current?.resetUpdate();
      gridRefs.current["grid2"].current?.resetUpdate();

      return true;
    };

    const onCellValueChangedH = async (event: CellValueChangedEvent) => {
      if (!event) return;
      const gridApiH = gridRefs.current["grid1"].current;
      const colId = event.colId;
      const rowData = event.rowData;
      const rowNode = event.rowNode;
      if (gridApiH) {
        const selectedDataH =
          gridRefs.current["grid1"].current.getSelectedRows()[0];
        if (selectedDataH.length > 0) {
          selectedDataH.forEach((row: any) => {
            if (row.cd_cust !== row._previousCdCust) {
              row.cd_cust_dlv = row.cd_cust; // cd_cust_dlv에 값을 넣음
            }
          });
        }
      }
    };
    const onCellValueChangedL = async (event: CellValueChangedEvent) => {
      if (!event) return;
      const colId = event.colId;
      const rowData = event.rowData;

      if (syncingFromHeaderRef.current) {
        return;
      }
      const focusedCellH = gridRefs.current["grid1"].current.focusedRowNode();
      // 수정된 컬럼 ID 및 정보

      // gridApiRefL에서 footerData 가져오기
      // await setFooterDataToRow();
    };

    useImperativeHandle(
      ref,
      () => ({
        getSelectedValue: async () => {
          const closeFlag = await onSave();
          return { excelUpload: closeFlag };
        },
      }),
      [onSave]
    );
    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onFocusedRowChanged: onFocusedRowChanged,
            onCellValueChanged: onCellValueChangedH,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {
            onCellValueChanged: onCellValueChangedL,
          },
        },
      }),
      [columns, dataSources]
    );
    return (
      <div style={{ position: "relative" }}>
        {/* 헤더 */}
        <div style={{ height: "32vh", marginTop: 10 }}>
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={headerColumns}
            rowData={[]}
            events={grids.grid1.events}
            etcProps={{
              rowSelection: "single",
              isEditable: true,
              popupParent: popupParent,
            }}
          />
        </div>

        {/* 디테일 */}
        <div style={{ height: "32vh", marginTop: 10 }}>
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={[]}
            events={grids.grid2.events}
            etcProps={{
              rowSelection: "single",
              isEditable: true,
              popupParent: popupParent,
            }}
          />
        </div>
      </div>
    );
  }
);
export default ExcelUpload_S02_ReviewSave;
